import { Input } from '@fountain/fountain-ui-components';
import {
  ButtonBase,
  List,
  ListItem,
  ListSubheader,
  Popover,
  Typography,
} from '@material-ui/core';
import { StandardQuestionTemplate } from 'api-clients/monolith';
import React, { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { messages } from './messages';
import { useStyles } from './styles';

export interface StandardKeyPickerProps {
  anchorEl: HTMLElement | null;
  templates: StandardQuestionTemplate[];
  /** Keys already placed on this form, shown but not selectable. */
  usedKeys: string[];
  onSelect: (template: StandardQuestionTemplate) => void;
  onCreateCustomKey: () => void;
  onClose: () => void;
}

const UNGROUPED = '__ungrouped__';

/**
 * Lists the standard questions an account can attach to a question, grouped by
 * their display group. Picking one binds the row to that key; the escape hatch
 * at the bottom turns the row into a custom key instead.
 */
export const StandardKeyPicker: FC<StandardKeyPickerProps> = ({
  anchorEl,
  templates,
  usedKeys,
  onSelect,
  onCreateCustomKey,
  onClose,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const visible = templates.filter(
      template =>
        !template.hidden &&
        (!needle ||
          template.title.toLowerCase().includes(needle) ||
          template.key.toLowerCase().includes(needle) ||
          template.question.toLowerCase().includes(needle)),
    );

    const byGroup = new Map<string, StandardQuestionTemplate[]>();
    visible.forEach(template => {
      const groupKey = template.display_group ?? UNGROUPED;
      byGroup.set(groupKey, [...(byGroup.get(groupKey) ?? []), template]);
    });

    return Array.from(byGroup.entries()).map(([groupKey, items]) => ({
      groupKey,
      label: items[0].display_group_label ?? items[0].group ?? '',
      items,
    }));
  }, [templates, query]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    >
      <div className={styles.picker} data-testid="standard-key-picker">
        <div className={styles.pickerHeader}>
          <Typography variant="h4">
            {intl.formatMessage(messages.standardKeysHeading)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {intl.formatMessage(messages.standardKeysDescription)}
          </Typography>
        </div>
        <div className={styles.pickerSearch}>
          <Input
            autoFocus
            fullWidth
            size="small"
            value={query}
            placeholder={intl.formatMessage(messages.searchStandardKeys)}
            inputProps={{
              'aria-label': intl.formatMessage(messages.searchStandardKeys),
            }}
            onChange={event => setQuery(event.target.value)}
          />
        </div>
        {groups.length === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            className={styles.pickerEmpty}
          >
            {intl.formatMessage(messages.noStandardKeyResults, { query })}
          </Typography>
        ) : (
          <List className={styles.pickerList} dense disablePadding>
            {groups.map(group => (
              <li key={group.groupKey}>
                <ul style={{ padding: 0 }}>
                  {group.label && (
                    <ListSubheader
                      disableSticky
                      disableGutters
                      className={styles.pickerGroupLabel}
                    >
                      <Typography variant="caption">{group.label}</Typography>
                    </ListSubheader>
                  )}
                  {group.items.map(template => {
                    const used = usedKeys.includes(template.key);
                    return (
                      <ListItem
                        key={template.key}
                        button
                        disabled={used}
                        className={styles.pickerItem}
                        onClick={() => onSelect(template)}
                      >
                        <Typography variant="body2">
                          {template.title}
                        </Typography>
                        <Typography
                          variant="caption"
                          className={styles.pickerItemKey}
                        >
                          {used
                            ? intl.formatMessage(messages.alreadyOnThisForm)
                            : template.key}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </ul>
              </li>
            ))}
          </List>
        )}
        <div className={styles.pickerFooter}>
          <ButtonBase onClick={onCreateCustomKey} disableRipple>
            <Typography variant="body2" color="primary">
              {intl.formatMessage(messages.createCustomKey)}
            </Typography>
          </ButtonBase>
          <Typography variant="caption" color="textSecondary" display="block">
            {intl.formatMessage(messages.createCustomKeyDescription)}
          </Typography>
        </div>
      </div>
    </Popover>
  );
};
